import React, { Component } from "react";
import { connect } from "react-redux";
import { PageHeader, Row, DatePicker, Select, Switch, Spin, Alert, Table, Empty, message, Tag } from "antd";
import { ArrowLeftOutlined, SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import moment from "moment";
import { DynamicCardForm, ButtonItem } from "../../../../Components/Items";
import SelectClientesItem from "../../../../Components/SelectClientesItem";
import SelectContratoCMMS from "../componentes/SelectContratoCMMS";
import FiltroIngenieroCMMS, { esperarFiltroDefault } from "../componentes/FiltroIngenieroCMMS";
import { Tile, FilaTiles } from "../componentes/IndicadoresCMMS";
import { cmmsPost, cargarSucursales, fechaDiaPayload, formatoFecha, formatoFechaHora, formatoMoneda, formatoPorcentaje } from "../cmmsComun";
import { reportePorClave } from "./definicionesReportes";
import { descargarReporteExcel } from "./excelReportesCMMS";
import "../cmms.css";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Pantalla generica de un reporte CMMS (/panel/cmmsReportes/:reporte): filtros, indicadores,
// tablas y Excel, todo a partir de su definicion en definicionesReportes.js.

const formatear = (v, tipo) => {
    if (v === null || v === undefined || v === "") return "";
    if (tipo === "moneda") return formatoMoneda(v);
    if (tipo === "porcentaje") return formatoPorcentaje(v);
    if (tipo === "fecha") return formatoFecha(v);
    if (tipo === "fechaHora") return formatoFechaHora(v);
    if (tipo === "numero") return typeof v === "number" ? v.toLocaleString("es-MX") : v;
    return String(v);
};
const alineacion = (tipo) => (["numero", "moneda", "porcentaje"].includes(tipo) ? "right" : tipo === "fecha" || tipo === "fechaHora" ? "center" : "left");

const NOMBRES_FILTRO = { clienteOContrato: "el cliente o el contrato", cliente: "el cliente", contrato: "el contrato" };

class ReporteCMMS extends Component {
    get def() {
        return reportePorClave(this.props.match?.params?.reporte);
    }

    state = {
        cargando: false,
        datos: null,
        rango: this.def?.rangoInicial ? this.def.rangoInicial() : [moment().startOf("year"), moment()],
        anio: moment(),
        clienteId: null,
        clienteNombre: "",
        contratoId: null,
        contratoNombre: "",
        empleadoId: null,
        empleadoNombre: "",
        direccionId: null,
        sucursales: [],
        conContrato: 0,
        estatusServicio: "concluidos",
        tipoRefaccion: "todas",
        soloPendientes: true,
        soloAbiertos: false,
    };

    componentDidMount() {
        // Si el usuario fijó un ingeniero, los reportes que filtran por ingeniero arrancan con él.
        esperarFiltroDefault(this, "empleadoId", "empleadoNombre");
    }

    componentDidUpdate(prev) {
        // Otro reporte en la misma ruta: se reinicia.
        if (prev.match?.params?.reporte !== this.props.match?.params?.reporte) this.setState({ datos: null, rango: this.def?.rangoInicial ? this.def.rangoInicial() : [moment().startOf("year"), moment()] });
    }

    tiene = (f) => (this.def?.filtros || []).includes(f);

    cambiarCliente = async (clienteId, cliente) => {
        this.setState({ clienteId: clienteId || null, clienteNombre: cliente?.stringBuscar || "", contratoId: null, contratoNombre: "", direccionId: null, datos: null });
        if (this.tiene("sucursal")) this.setState({ sucursales: clienteId ? await cargarSucursales(this.props, clienteId) : [] });
    };

    faltante = () => {
        for (const r of this.def.requeridos || []) {
            if (r === "clienteOContrato" && !this.state.clienteId && !this.state.contratoId) return r;
            if (r === "cliente" && !this.state.clienteId) return r;
            if (r === "contrato" && !this.state.contratoId) return r;
        }
        return null;
    };

    consultar = async () => {
        const falta = this.faltante();
        if (falta) return message.warning(`Elija ${NOMBRES_FILTRO[falta]}`);
        const s = this.state;
        this.setState({ cargando: true });
        try {
            const res = await cmmsPost(this.props, this.def.endpoint, {
                desde: fechaDiaPayload(s.rango?.[0]),
                hasta: fechaDiaPayload(s.rango?.[1]),
                anio: s.anio ? s.anio.year() : null,
                clienteId: s.clienteId,
                contratoId: s.contratoId,
                empleadoId: this.tiene("ingeniero") ? s.empleadoId : null,
                direccionId: s.direccionId,
                conContrato: s.conContrato,
                estatus: s.estatusServicio,
                tipo: s.tipoRefaccion,
                soloPendientes: s.soloPendientes,
                soloAbiertos: s.soloAbiertos,
            });
            if (res.data.success === 2) this.setState({ datos: res.data });
            else message.error(res.data.message || "No se pudo generar el reporte");
        } catch (error) {
            console.log(error);
            message.error("No se pudo generar el reporte");
        } finally {
            this.setState({ cargando: false });
        }
    };

    descripcionFiltros = () => {
        const s = this.state;
        const partes = [];
        if (this.tiene("rango")) partes.push(`Periodo: ${s.rango[0].format("DD/MM/YYYY")} a ${s.rango[1].format("DD/MM/YYYY")}`);
        if (this.tiene("anio")) partes.push(`Año: ${s.anio.year()}`);
        if (s.clienteNombre) partes.push(`Cliente: ${s.clienteNombre}`);
        if (s.contratoNombre) partes.push(`Contrato: ${s.contratoNombre}`);
        // Con un contrato elegido el backend ignora "con / sin contrato".
        if (this.tiene("conContrato") && s.conContrato && !s.contratoId) partes.push(s.conContrato === 1 ? "Sólo con contrato" : "Sólo sin contrato");
        if (s.direccionId) partes.push(`Sucursal: ${s.sucursales.find((x) => x._id === s.direccionId)?.nombre || ""}`);
        if (this.tiene("ingeniero") && s.empleadoId) partes.push(`Ingeniero: ${s.empleadoNombre || "uno"}`);
        return partes.join(" · ");
    };

    excel = (vista) =>
        descargarReporteExcel({ titulo: this.def.titulo, tablas: vista.tablas, empresaNombre: this.props.empresaNombre, descripcionFiltros: this.descripcionFiltros() });

    renderFiltros() {
        const s = this.state;
        const f = (nombre, contenido, ancho = 240) => (
            <div style={{ width: ancho }}>
                <div className="cmms-etiqueta-campo">{nombre}</div>
                {contenido}
            </div>
        );
        return (
            <div className="cmms-filtros" style={{ alignItems: "flex-end" }}>
                {this.tiene("rango") && f("Periodo", <RangePicker format="DD/MM/YYYY" allowClear={false} value={s.rango} onChange={(rango) => rango && this.setState({ rango, datos: null })} style={{ width: "100%" }} />, 260)}
                {this.tiene("anio") && f("Año", <DatePicker picker="year" allowClear={false} value={s.anio} onChange={(anio) => anio && this.setState({ anio, datos: null })} style={{ width: "100%" }} />, 120)}
                {this.tiene("estatusServicio") &&
                    f(
                        "Órdenes",
                        <Select value={s.estatusServicio} onChange={(estatusServicio) => this.setState({ estatusServicio, datos: null })} style={{ width: "100%" }}>
                            <Option value="concluidos">Concluidas</Option>
                            <Option value="abiertos">Abiertas</Option>
                            <Option value="cancelados">Canceladas</Option>
                            <Option value="todos">Todas</Option>
                        </Select>,
                        140
                    )}
                {this.tiene("cliente") &&
                    f(
                        `Cliente${(this.def.requeridos || []).includes("cliente") ? " *" : ""}`,
                        <SelectClientesItem empresaId={this.props.empresaId} headersToken={this.props.headersToken} width="100%" allowClear placeholder="Todos" value={s.clienteId || undefined} onChange={this.cambiarCliente} />,
                        260
                    )}
                {this.tiene("contrato") &&
                    f(
                        `Contrato${(this.def.requeridos || []).includes("contrato") ? " *" : ""}`,
                        <SelectContratoCMMS
                            empresaId={this.props.empresaId}
                            headersToken={this.props.headersToken}
                            clienteId={s.clienteId}
                            placeholder="Todos"
                            value={s.contratoId}
                            onChange={(contratoId, c) => this.setState({ contratoId, contratoNombre: c?.nombre || "", datos: null })}
                        />,
                        240
                    )}
                {this.tiene("sucursal") &&
                    f(
                        "Sucursal",
                        <Select allowClear placeholder={s.clienteId ? "Todas" : "Elija el cliente"} disabled={!s.clienteId} value={s.direccionId || undefined} onChange={(direccionId) => this.setState({ direccionId: direccionId || null, datos: null })} style={{ width: "100%" }}>
                            {s.sucursales.map((x) => (
                                <Option key={x._id} value={x._id}>
                                    {x.nombre}
                                </Option>
                            ))}
                        </Select>,
                        220
                    )}
                {this.tiene("conContrato") &&
                    f(
                        "Contrato",
                        <Select value={s.conContrato} disabled={!!s.contratoId} onChange={(conContrato) => this.setState({ conContrato, datos: null })} style={{ width: "100%" }}>
                            <Option value={0}>Con y sin contrato</Option>
                            <Option value={1}>Sólo con contrato</Option>
                            <Option value={2}>Sólo sin contrato</Option>
                        </Select>,
                        170
                    )}
                {this.tiene("ingeniero") &&
                    f(
                        "Ingeniero",
                        <FiltroIngenieroCMMS
                            {...this.props}
                            width="100%"
                            placeholder="Todos"
                            value={s.empleadoId}
                            onChange={(empleadoId, empleado) => this.setState({ empleadoId, empleadoNombre: empleado?.stringBuscar || "", datos: null })}
                        />,
                        270
                    )}
                {this.tiene("tipoRefaccion") &&
                    f(
                        "Refacciones",
                        <Select value={s.tipoRefaccion} onChange={(tipoRefaccion) => this.setState({ tipoRefaccion, datos: null })} style={{ width: "100%" }}>
                            <Option value="todas">Todas</Option>
                            <Option value="bajo">Bajo contrato</Option>
                            <Option value="fuera">Fuera de contrato</Option>
                        </Select>,
                        160
                    )}
                {this.tiene("soloPendientes") &&
                    f("Preventivos", <Switch checked={s.soloPendientes} checkedChildren="Sólo pendientes" unCheckedChildren="Todo el programa" onChange={(soloPendientes) => this.setState({ soloPendientes, datos: null })} />, 160)}
                {this.tiene("soloAbiertos") && f("Tickets", <Switch checked={s.soloAbiertos} checkedChildren="Sólo abiertos" unCheckedChildren="Todos" onChange={(soloAbiertos) => this.setState({ soloAbiertos, datos: null })} />, 120)}
                <ButtonItem type="primary" icon={<SearchOutlined />} loading={this.state.cargando} onClick={this.consultar}>
                    Generar
                </ButtonItem>
            </div>
        );
    }

    renderTabla(tb) {
        const dataSource = tb.filas.map((fila, i) => Object.fromEntries([["key", i], ...fila.map((v, j) => [`c${j}`, v])]));
        const anchoTotal = tb.columnas.reduce((a, c) => a + (c.ancho || 16) * 8, 0);
        return (
            <DynamicCardForm key={tb.titulo} title={`${tb.titulo} (${tb.filas.length})`} xs={24}>
                {tb.nota ? <Alert type="warning" showIcon message={tb.nota} style={{ marginBottom: 8 }} /> : null}
                <Table
                    size="small"
                    dataSource={dataSource}
                    pagination={tb.filas.length > 50 ? { pageSize: 50, size: "small", showSizeChanger: true, pageSizeOptions: ["50", "100", "500"] } : false}
                    scroll={{ x: anchoTotal }}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin datos para estos filtros" /> }}
                    columns={tb.columnas.map((c, j) => ({
                        title: c.titulo,
                        dataIndex: `c${j}`,
                        width: (c.ancho || 16) * 8,
                        align: alineacion(c.tipo),
                        fixed: j === 0 && tb.columnas.length > 8 ? "left" : undefined,
                        render: (v) => formatear(v, c.tipo),
                        sorter: (a, b) => {
                            const x = a[`c${j}`];
                            const y = b[`c${j}`];
                            if (typeof x === "number" || typeof y === "number") return (Number(x) || 0) - (Number(y) || 0);
                            return String(x ?? "").localeCompare(String(y ?? ""));
                        },
                    }))}
                    summary={
                        tb.totales && tb.filas.length
                            ? () => (
                                  <Table.Summary.Row className="cmms-fila-total">
                                      {tb.totales.map((v, j) => (
                                          <Table.Summary.Cell key={j} index={j} align={alineacion(tb.columnas[j]?.tipo)}>
                                              {formatear(v, j === 0 ? "texto" : tb.columnas[j]?.tipo)}
                                          </Table.Summary.Cell>
                                      ))}
                                  </Table.Summary.Row>
                              )
                            : undefined
                    }
                />
            </DynamicCardForm>
        );
    }

    render() {
        const def = this.def;
        if (!def || def.deshabilitado) return <Alert type="warning" showIcon message="Este reporte no existe o todavía no está disponible" />;
        const vista = this.state.datos ? def.construir(this.state.datos) : null;
        return (
            <div className="cmms-pagina">
                <PageHeader
                    title={def.titulo}
                    subTitle={<Tag>{def.origen}</Tag>}
                    onBack={() => this.props.history.push("/panel/cmmsReportes")}
                    backIcon={<ArrowLeftOutlined />}
                    style={{ border: "1px solid rgb(235, 237, 240)", backgroundColor: "white", marginBottom: 10 }}
                    extra={
                        <ButtonItem type="default" icon={<FileExcelOutlined />} disabled={!vista} onClick={() => this.excel(vista)}>
                            Descargar Excel
                        </ButtonItem>
                    }
                />
                <div className="cmms-ayuda" style={{ margin: "0 0 8px" }}>
                    {def.descripcion}
                </div>
                {this.renderFiltros()}
                {def.nota ? <Alert type="info" showIcon message={def.nota} style={{ marginBottom: 10 }} /> : null}
                <Spin spinning={this.state.cargando}>
                    {!vista ? (
                        <Empty style={{ padding: 40, background: "#fff", borderRadius: 8 }} description='Elija los filtros y dé "Generar"' />
                    ) : (
                        <>
                            {vista.tiles?.length ? (
                                <FilaTiles>
                                    {vista.tiles.map((x) => (
                                        <Tile key={x.titulo} titulo={x.titulo} valor={x.valor} detalle={x.detalle} />
                                    ))}
                                </FilaTiles>
                            ) : null}
                            <Row gutter={14}>{vista.tablas.map((tb) => this.renderTabla(tb))}</Row>
                        </>
                    )}
                </Spin>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
    empresaNombre: state.empresa.nombre,
});

export default connect(mapStateToProps)(ReporteCMMS);
